/**
 * Wire protocol for the in-app terminal websockets. Two servers speak it:
 * - the E2B sandbox terminal (`/sprite/sandbox_terminal/ws`, backend counterpart in
 *   `backend/lb/features/tools/sandbox_e2b/sandbox_terminal.py`)
 * - the on-device PTY host (`apps/desktop/pty-host/pty-host.ts`, loopback websocket)
 *
 * - client -> server BINARY frames: raw terminal input bytes.
 * - client -> server TEXT frames: JSON control messages (auth first, then resize).
 * - server -> client BINARY frames: raw PTY output bytes.
 * - server -> client TEXT frames: JSON status messages (`{"type": "auth_ack"}`).
 *
 * The URL carries no secret — auth happens at connection time via the first
 * text frame, validated server-side (token + internal gate + chat ownership).
 */

export function buildSandboxTerminalWsUrl(
  apiBaseUrl: string,
  chatId: string
): string {
  const url = new URL(apiBaseUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = `${url.pathname.replace(/\/$/, '')}/sprite/sandbox_terminal/ws`;
  url.search = new URLSearchParams({ chat_id: chatId }).toString();
  return url.toString();
}

export function encodeAuthMessage(token: string): string {
  return JSON.stringify({ type: 'auth', token });
}

/** Local PTY host: `ws://127.0.0.1:<port>/pty`; the grid rides along so the shell spawns at size. */
export function buildLocalTerminalWsUrl(
  port: number,
  cols: number,
  rows: number
): string {
  const url = new URL(`ws://127.0.0.1:${port}/pty`);
  url.search = new URLSearchParams({
    cols: String(cols),
    rows: String(rows),
  }).toString();
  return url.toString();
}

/** `session` attaches to an existing host session; omitted (or unknown to the host) creates one. */
export function encodeLocalTerminalAuthMessage(
  token: string,
  cols: number,
  rows: number,
  session?: string,
  options?: {
    /** Only attach to `session`; if the host no longer has it, fail (4404) instead of spawning a shell. */
    attachOnly?: boolean;
  }
): string {
  return JSON.stringify({
    type: 'auth',
    token,
    cols,
    rows,
    session,
    attachOnly: options?.attachOnly || undefined,
  });
}

export function encodeResizeMessage(cols: number, rows: number): string {
  return JSON.stringify({ type: 'resize', cols, rows });
}

/** Local host only: end the session for every attached client (the shell gets SIGHUP). */
export function encodeKillMessage(): string {
  return JSON.stringify({ type: 'kill' });
}

/**
 * User-facing copy for a websocket close, keyed on the close code. The backend `reason` strings
 * can carry technical detail (sandbox connect errors, ids), so we map by code and never surface
 * the raw reason. Codes mirror sprite_terminal_router.py (4xxx = app-defined).
 */
export function describeSandboxTerminalClose(code: number): string {
  switch (code) {
    case 4001:
    case 4002:
    case 4003:
      return 'Could not authenticate the terminal';
    case 4403:
      return 'Sandbox terminal is available to internal users only';
    case 4404:
      return 'This chat has no sandbox yet';
    case 4424:
      return 'Could not connect to the sandbox';
    default:
      return 'Disconnected';
  }
}

/** Close codes from pty-host.ts. 1000 follows an `exit` frame and is described from that instead. */
export function describeLocalTerminalClose(code: number): string {
  switch (code) {
    case 4001:
      return 'Could not authenticate the terminal';
    case 4404:
      return 'The process had already ended';
    case 4424:
      return 'Could not start a shell';
    default:
      return 'Disconnected';
  }
}

function parseControlFrame(raw: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export interface AuthAck {
  /** Local host: id of the session this socket is attached to. Sandbox: absent. */
  session?: string;
  /** Local host: true when attached to a pre-existing session (history was replayed). */
  restored?: boolean;
  /** Local host: the session's current title, so a reattach shows it at once. */
  title?: string;
  /** Local host: the PTY's grid after this auth was applied. Lets the client skip redundant resizes. */
  cols?: number;
  rows?: number;
}

export function parseAuthAckFrame(raw: string): AuthAck | null {
  const frame = parseControlFrame(raw);
  if (frame?.type !== 'auth_ack') return null;
  return {
    session: typeof frame.session === 'string' ? frame.session : undefined,
    restored: typeof frame.restored === 'boolean' ? frame.restored : undefined,
    title: typeof frame.title === 'string' ? frame.title : undefined,
    cols: typeof frame.cols === 'number' ? frame.cols : undefined,
    rows: typeof frame.rows === 'number' ? frame.rows : undefined,
  };
}

/** `{"type":"title","title"}` from the local host when the shell sets its window title. */
export function parseTitleFrame(raw: string): string | null {
  const frame = parseControlFrame(raw);
  if (frame?.type !== 'title') return null;
  return typeof frame.title === 'string' ? frame.title : null;
}

/** `{"type":"exit","code":n}` sent by the PTY host right before a normal close. */
export function parseExitFrame(raw: string): { code: number | null } | null {
  const frame = parseControlFrame(raw);
  if (frame?.type !== 'exit') return null;
  return { code: typeof frame.code === 'number' ? frame.code : null };
}
